import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Form,
  FormField,
  Input,
  Toggle,
  Alert,
} from '@cloudscape-design/components';
import { useState, useEffect } from 'react';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import { BusinessUnitValidator } from 'src/validations/BusinessUnitValidator';
import RequiredFieldLabel from '../RequiredFieldLabel';

interface BusinessUnitFormProps {
  visible: boolean;
  onSubmit: (data: any) => void;
  onDismiss: () => void;
  apiStatus: ApiRequestStatus;
}

export interface BusinessUnitFormData {
  name: string;
  description: string;
  isActive: boolean;
}

const BusinessUnitForm = ({
  visible,
  onDismiss,
  apiStatus,
  onSubmit,
}: BusinessUnitFormProps) => {
  const [formData, setFormData] = useState<BusinessUnitFormData>({
    name: '',
    description: '',
    isActive: true,
  });

  const { fieldValidationError, validate, setFieldValidationErrors } = useFormValidation(
    formData,
    BusinessUnitValidator(),
  );

  // Reset form when API call is successful
  useEffect(() => {
    if (apiStatus.isSuccess) {
      resetForm();
    }
  }, [apiStatus.isSuccess]);

  // Clean up when form is closed from the view
  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true,
    });

    setFieldValidationErrors({});
  };

  const handleCancelClick = () => {
    onDismiss();
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
    });
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancelClick}
      header="Add New Business Unit"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="cancel-button"
              formAction="none"
              variant="link"
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleSubmit}
              loading={apiStatus.isLoading}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form data-testid="business-unit-form">
        <SpaceBetween direction="vertical" size="l">
          <FormField
            data-testid="business-unit-name-field"
            label={<RequiredFieldLabel label="Business Unit Name" />}
            errorText={fieldValidationError.name}
          >
            <Input
              data-testid="business-unit-name-input"
              value={formData.name}
              onChange={({ detail }) =>
                setFormData((prevState) => ({
                  ...prevState,
                  name: detail.value,
                }))
              }
              placeholder="Enter business unit name (spaces allowed)"
            />
          </FormField>

          <FormField
            data-testid="business-unit-description-field"
            label={<RequiredFieldLabel label="Description" />}
            errorText={fieldValidationError.description}
          >
            <Input
              data-testid="business-unit-description-input"
              value={formData.description}
              onChange={({ detail }) =>
                setFormData((prevState) => ({
                  ...prevState,
                  description: detail.value,
                }))
              }
              placeholder="Enter description"
            />
          </FormField>

          <FormField
            label="Activation Status"
            constraintText="Enable or disable the business unit using this switch."
          >
            <Toggle
              onChange={({ detail }) => {
                setFormData((prevState) => ({
                  ...prevState,
                  isActive: detail.checked,
                }));
              }}
              checked={formData.isActive}
            >
              {formData.isActive ? 'Active' : 'Inactive'}
            </Toggle>
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default BusinessUnitForm;
